import React from 'react';
import { useTranslationX } from 'i18n';
import { getMasked } from 'utils/inputMask';

import styles from './GeneralCompositionTable.module.scss';

type Props = {
  composicao: {
    [key: string]: {
      modalidadeEmbrapii: number;
      outros: number;
      total: number;
      modalidadeEmbrapiiPercentage?: number;
      outrosPercentage?: number;
    };
  };
};

const GeneralCompositionTable = ({ composicao }: Props) => {
  const { tx, t } = useTranslationX('update_wp:financial', ['translation', 'update_wp']);

  let i = 0;

  const composicaoWithPercentages = Object.keys(composicao).reduce(
    (acc, key) => {
      const item = composicao[key];
      const modalidadeEmbrapiiTotal = Object.values(composicao).reduce(
        (sum, curr) => sum + curr.modalidadeEmbrapii,
        0
      );
      const outrosTotal = Object.values(composicao).reduce(
        (sum, curr) => sum + curr.outros,
        0
      );

      acc[key] = {
        total: {
          value: item.total,
          percentage: 0, // Total percentage is not calculated row-wise
        },
        modalidadeEmbrapii: {
          value: item.modalidadeEmbrapii,
          percentage: modalidadeEmbrapiiTotal
            ? parseFloat(
                ((item.modalidadeEmbrapii / modalidadeEmbrapiiTotal) * 100).toFixed(2)
              )
            : 0,
        },
        outros: {
          value: item.outros,
          percentage: outrosTotal
            ? parseFloat(((item.outros / outrosTotal) * 100).toFixed(2))
            : 0,
        },
      };
      return acc;
    },
    {} as Record<
      string,
      {
        modalidadeEmbrapii: { value: number; percentage: number };
        outros: { value: number; percentage: number };
        total: { value: number; percentage: number };
      }
    >
  );

  const keys = Object.keys(composicaoWithPercentages);

  const partList = keys.map(p =>
    i === keys.length - 1 ? (
      <th key={i++} colSpan={2} className={styles.partItem}>
        {`${p} (${tx('composition.economic')})`}
      </th>
    ) : (
      <th key={i++} colSpan={2} className={styles.partItem}>
        {p}
      </th>
    )
  );

  const labelKeys = ['modalidadeEmbrapii', 'outros', 'total'];

  let j = 0;

  //

  const partLabelVal = (
    <>
      <tr key={i++}>
        <th key={i++}></th>
        {partList}
      </tr>
      <tr key={i++}>
        <th key={i++}></th>
        {keys.map(p => (
          <React.Fragment key={i++}>
            <td key={i++}>{`${tx('composition.currency')} (${t('currency')})`}</td>
            <td key={i++}>{tx('composition.percent')}</td>
          </React.Fragment>
        ))}
      </tr>
    </>
  );

  const compositionList = labelKeys.map((key: string) => {
    const mappedValues2 = Object.values(composicaoWithPercentages).map(value => {
      return value[key as keyof typeof value] as { value: number; percentage: number };
    });

    return (
      <tr key={i++}>
        <td className={styles.labelKeys} key={i++}>
          {tx(`composition.${labelKeys[j++]}`)}
        </td>
        {mappedValues2.map(value => {
          return (
            <>
              <td key={i++}>
                {value.value !== 0
                  ? getMasked(value.value.toFixed(2), {
                      pattern: 'currency',
                    })
                  : ''}
              </td>
              <td key={i++}>
                {value.percentage !== 0
                  ? getMasked(value.percentage.toFixed(2), {
                      pattern: 'currency',
                    })
                  : ''}
              </td>
            </>
          );
        })}
      </tr>
    );
  });

  return (
    <>
      <h4>{tx('composition.title')}</h4>

      <table className={styles.financial}>
        <thead>{partLabelVal}</thead>
        <tbody>{compositionList}</tbody>
      </table>
    </>
  );
};

export default GeneralCompositionTable;
